import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography } from '@mui/material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { AuthContext } from '../context/auth-context';

// Shared look (design tokens) - same as the rest of IZHub
const ui = {
  primary: '#009db9',
  primaryDark: '#007a8a',
  primarySoft: '#e6f6f9',
  text: '#1f2933',
  muted: '#6b7280',
  border: '#e5e7eb',
  surface: '#f7f9fb',
  shadow: '0 4px 20px rgba(16, 24, 40, 0.06)',
};

const InfoTile = ({ icon: Icon, label, value }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 2,
      minWidth: 0,
      borderRadius: '12px',
      backgroundColor: ui.surface,
      border: `1px solid ${ui.border}`,
    }}
  >
    <Box
      sx={{
        flexShrink: 0,
        width: 40,
        height: 40,
        borderRadius: '10px',
        backgroundColor: ui.primarySoft,
        color: ui.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon fontSize="small" />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: ui.muted }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 600, color: ui.text, fontSize: '0.95rem', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const UserProfile = ({
  name: initialName,
  email: initialEmail,
  jobTitle: initialJobTitle,
  department: initialDepartment,
  employeeId: initialEmployeeId,
}) => {
  const { user } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    name: initialName || user?.name || '',
    email: initialEmail || user?.email || '',
    jobTitle: initialJobTitle || '',
    department: initialDepartment || '',
  });

  // Syncs form data with prop changes
  useEffect(() => {
    setFormData({
      name: initialName || user?.name || '',
      email: initialEmail || user?.email || '',
      jobTitle: initialJobTitle || '',
      department: initialDepartment || '',
    });
  }, [initialName, initialEmail, initialJobTitle, initialDepartment, user]);

  const displayName = user?.name || formData.name || 'User';

  return (
    <Box
      sx={{
        mb: 3,
        borderRadius: '16px',
        border: `1px solid ${ui.border}`,
        backgroundColor: '#fff',
        boxShadow: ui.shadow,
        overflow: 'hidden',
      }}
    >
      {/* Identity header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
          p: { xs: 2.5, sm: 3.5 },
          background: 'linear-gradient(135deg, #e6f6f9 0%, #ffffff 70%)',
          borderBottom: `1px solid ${ui.border}`,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: { xs: 56, sm: 68 },
            height: { xs: 56, sm: 68 },
            borderRadius: '18px',
            background: 'linear-gradient(135deg, #009db9 0%, #007a8a 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: { xs: '1.4rem', sm: '1.7rem' },
            boxShadow: '0 6px 16px rgba(0, 157, 185, 0.3)',
          }}
        >
          {displayName.charAt(0).toUpperCase()}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '1.35rem', sm: '1.75rem' },
              lineHeight: 1.25,
              wordBreak: 'break-word',
            }}
          >
            <a href="/profile" style={{ color: ui.primaryDark, textDecoration: 'none' }}>
              {displayName}
            </a>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, color: ui.muted }}>
            <WorkOutlineIcon sx={{ fontSize: '1.05rem' }} />
            <Typography sx={{ fontWeight: 500, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              {formData.jobTitle || 'Your Job Title'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Details */}
      <Box
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 2,
        }}
      >
        <InfoTile icon={BadgeOutlinedIcon} label="Employee ID" value={initialEmployeeId || 'N/A'} />
        <InfoTile icon={EmailOutlinedIcon} label="Email" value={formData.email || 'N/A'} />
        <InfoTile icon={ApartmentOutlinedIcon} label="Department" value={formData.department || 'N/A'} />
      </Box>
    </Box>
  );
};

export default UserProfile;